import api from '../services/api';
import { getCart } from '../components/cart';
import { setFreeShipping } from '../utils/bar-progress';
import { classifyPromocode, getB2BError } from '../utils/promocode-status';

class CartPromocode extends HTMLElement {
  constructor() {
    super();
    this._initializeElements();
  }

  _initializeElements() {
    this.parentSectionId = this.closest('js-cart')?.dataset.sectionId;
    this.promocodeInput = this.querySelector("[data-promocode-input]");
    this.promocodeButton = this.querySelector("[data-promocode-button]");
    this.promocodeError = this.querySelector("[data-promocode-error]");
    this.promocodeSuccess = this.querySelector("[data-promocode-success]");
  }

  connectedCallback() {
    this._setupEventListeners();
  }

  _setupEventListeners() {
    if (!this.promocodeButton || !this.promocodeInput) {
      console.warn("Promocode button or input not found");
      return;
    }

    this.promocodeButton.addEventListener("click", this._handlePromoActivator.bind(this));
    this.promocodeInput.addEventListener("focus", this._handleInputFocus.bind(this));
  }

  _handlePromoActivator(event) {
    event.preventDefault();
    const promocode = this.promocodeInput.value.toUpperCase().trim();

    if (promocode) {
      this.addPromo(promocode);
    }
  }

  _handleInputFocus() {
    this._hideError();
    this._hideSuccess();
  }

  async addPromo(promocode) {
    const strings = window.cartStrings.promocode;
    const withCode = (message) => message.replace('[code]', promocode);

    try {
      this.promocodeInput.value = "";

      const cartBefore = await api.getCart();
      const existingGift = this._getExistingGiftItem(cartBefore);

      if (existingGift?.properties?._promocode === promocode) {
        this._showSuccess(withCode(strings.success));
        return;
      }

      const isB2BCustomer = window.isCustomerB2B === 'true';
      const b2bError = getB2BError(promocode, isB2BCustomer);
      if (b2bError) {
        this._showError(withCode(strings[b2bError]));
        return;
      }

      const cartUpdate = await api.applyDiscount(promocode, this.parentSectionId);
      const discountCode = cartUpdate?.discount_codes?.find(
        (discount) => discount.code === promocode
      );
      const applicable = Boolean(discountCode?.applicable);
      const otherCodeApplied = Boolean(
        cartUpdate?.discount_codes?.some((discount) => discount.code !== promocode && discount.applicable)
      );

      // The service is only needed to refine the message: a failure here must
      // never block a code Shopify already accepted (classifyPromocode handles
      // an undefined response).
      const service = await api.getGiftCodeData(promocode);

      const result = classifyPromocode({
        code: promocode,
        applicable,
        service,
        isB2BCustomer,
        otherCodeApplied,
      });

      switch (result.status) {
        case 'b2b_code_error':
        case 'b2c_code_error':
          this._showError(withCode(strings[result.status]));
          return;
        case 'app_pending':
          this._showError(withCode(strings.app_pending));
          return;
        case 'minimum_not_reached':
          this._showError(withCode(strings.min_not_reached));
          return;
        case 'invalid':
          this._showError(withCode(strings.error));
          return;
        case 'gift': {
          const giftAdded = await this._handleGiftCode(service, promocode);
          if (!giftAdded) return;
          await getCart();
          break;
        }
        default:
          await getCart();
          if (result.freeShipping) setFreeShipping();
      }

      this._hideError();
      this._showSuccess(withCode(result.freeShipping ? strings.success_shipping : strings.success));
    } catch (error) {
      console.error(`Promocode ${promocode} failed`, error);
      this._showError(withCode(strings.error));
    }
  }

  async _handleGiftCode(data, code) {
    if (!data.giftProductId || typeof data.giftProductId !== 'string') return true;
  
    const codeSettings = data.metafield?.value ? JSON.parse(data.metafield.value) : {};
    const variantId = data.giftProductId.split('/').pop();
  
    const cartData = await api.getCart();
    const existingGift = this._getExistingGiftItem(cartData);

    const minQty = parseInt(codeSettings.minCartQuantity, 10);
    const requireMinCartQuantity = Boolean(codeSettings.requireMinCartQuantity);
    if (requireMinCartQuantity && cartData.item_count < minQty) {
      this._showError(`${window.cartStrings.promocode.qty_error} ${minQty}`);
      return false;
    }
  
    const minPrice = parseInt(codeSettings.minCartPrice, 10);
    const requireMinCartPrice = Boolean(codeSettings.requireMinCartPrice);
    if (requireMinCartPrice && cartData.total_price < minPrice * 100) {
      this._showError(`${window.cartStrings.promocode.price_error} ${minPrice}€`);
      return false;
    }

    if (existingGift && existingGift.properties?._promocode !== code) {
      await this._removeCartItemByKey(existingGift.key);
    }
  
    // Build gift properties (line item properties)
    const giftProperties = this._buildGiftProperties({
      code,
      requireMinCartPrice,
      minCartPrice: minPrice,
      requireMinCartQuantity,
      minCartQuantity: minQty,
    });
  
    const items = [
      {
        id: variantId,
        quantity: 1,
        properties: giftProperties,
        selling_plan: null
      }
    ];
  
    await api.addToCart({ items });
    return true;
  }


  _getExistingGiftItem(cart) {
    return cart?.items?.find((item) => item?.properties?._gift === 'true') || null;
  }
  
  async _removeCartItemByKey(lineKey) {
    const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
  
    if (!res.ok) {
      const err = await res.text();
      throw new Error(`Failed to remove gift item: ${err}`);
    }
  
    return res.json();
  }
  
  _buildGiftProperties({ code, requireMinCartPrice, minCartPrice, requireMinCartQuantity, minCartQuantity }) {
    const properties = {
      _gift: 'true',
      _promocode: code,
    };
  
    if (requireMinCartPrice) {
      properties._minCartPrice = `${minCartPrice}`;
    }
  
    if (requireMinCartQuantity) {
      properties._minCartQuantity = `${minCartQuantity}`;
    }
  
    return properties;
  }

  _decodeHtmlEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  _showError(message) {
    if (this.promocodeError) {
      this.promocodeError.textContent = this._decodeHtmlEntities(message);
      this.promocodeError.ariaHidden = false;
    }
  }

  _hideError() {
    if (this.promocodeError) {
      this.promocodeError.ariaHidden = true;
    }
  }

  _showSuccess(message) {
    if (this.promocodeSuccess) {
      this.promocodeSuccess.textContent = this._decodeHtmlEntities(message);
      this.promocodeSuccess.ariaHidden = false;
    }
  }

  _hideSuccess() {
    if (this.promocodeSuccess) {
      this.promocodeSuccess.ariaHidden = true;
    }
  }
}

customElements.define("cart-promocode", CartPromocode);